import { HttpStatusEnum } from 'submodules/nerit-framework-utils/sdk-utils/request-manager/enums/HttpStatusEnum'
import { ThemeProject } from 'config/theme/project/ThemeProject'
import { ThemeFrameworkCommon } from 'submodules/nerit-framework-ui/theme/framework/ThemeFrameworkCommon'

export const LOGIN_EMPTY_FIELDS_MESSAGE = 'Preencha todos os campos para prosseguir'
export const LOGIN_INVALID_CREDENTIALS_MESSAGE = 'Usuário ou Senha inválidos'
export const LOGIN_CAPS_LOCK_MESSAGE = 'Caps Lock está ativado'
export const LOGIN_ALERT_COLOR = ThemeProject.error
export const LOGIN_ALERT_FONT_SIZE = 'normal' as const
export const LOGIN_ALERT_FONT_SIZE_REM = ThemeFrameworkCommon.fontSizes[LOGIN_ALERT_FONT_SIZE]
export const LOGIN_PASSWORD_TOGGLE_ATTR = 'data-login-password-toggle'
export const LOGIN_PASSWORD_TOGGLE_HOST_FIELD = 'password'
export const LOGIN_WELCOME_TITLE_PREFIX = 'Seja bem vindo,'
export const LOGIN_WELCOME_TITLE_EMPHASIS = 'Faça seu login para prosseguir'
export const LOGIN_WELCOME_TITLE = `${LOGIN_WELCOME_TITLE_PREFIX} ${LOGIN_WELCOME_TITLE_EMPHASIS}`
export const LOGIN_USERNAME_FIELD = 'username'
export const LOGIN_PASSWORD_FIELD = 'password'
export const LOGIN_ENTER_SUBMIT_FIELDS: ReadonlyArray<'username' | 'password'> = [LOGIN_USERNAME_FIELD, LOGIN_PASSWORD_FIELD]
export const LOGIN_VIEW_SECTION_ORDER = ['welcomeTitle', 'alertRegion', 'usernameField'] as const
export const LOGIN_CAPS_LOCK_LISTEN_EVENTS: ReadonlyArray<'keydown' | 'keyup' | 'mousemove'> = ['keydown', 'keyup', 'mousemove']

export type LoginSubmitAlertTP = 'emptyFields' | 'invalidCredentials'

export type LoginPasswordVisibilityTP = 'hidden' | 'visible'

export type LoginSubmitPlanTP = {
    submitAlert?: LoginSubmitAlertTP,
    shouldRunLoginRequest: boolean,
    shouldShowEmptyFieldsToast: boolean,
}

export type LoginRequestOutcomePlanTP = {
    submitAlert?: LoginSubmitAlertTP,
    shouldShowCredentialsToast: boolean,
    shouldNavigateHome: boolean,
}

export function isBlankLoginValue(value?: string): boolean {
    return !value || value.trim() === ''
}

export function hasBlankLoginField(username?: string, password?: string): boolean {
    return isBlankLoginValue(username) || isBlankLoginValue(password)
}

export function loginAlertMessages(params: { capsLock: boolean, submitAlert?: LoginSubmitAlertTP }): string[] {
    const messages: string[] = []

    if (params.capsLock)
        messages.push(LOGIN_CAPS_LOCK_MESSAGE)

    if (params.submitAlert === 'emptyFields')
        messages.push(LOGIN_EMPTY_FIELDS_MESSAGE)

    if (params.submitAlert === 'invalidCredentials')
        messages.push(LOGIN_INVALID_CREDENTIALS_MESSAGE)

    return messages
}

export function isLoginCredentialsFailure(status?: HttpStatusEnum): boolean {
    return status === HttpStatusEnum.UNAUTHORIZED
}

export function submitAlertAfterLoginFailure(status?: HttpStatusEnum): LoginSubmitAlertTP | undefined {
    if (isLoginCredentialsFailure(status))
        return 'invalidCredentials'

    return undefined
}

export function canStartLoginRequest(isAwaiting: boolean): boolean {
    return !isAwaiting
}

export function togglePasswordVisibility(current: LoginPasswordVisibilityTP): LoginPasswordVisibilityTP {
    return current === 'hidden' ? 'visible' : 'hidden'
}

export function passwordInputType(visibility: LoginPasswordVisibilityTP): 'password' | 'text' {
    return visibility === 'hidden' ? 'password' : 'text'
}

export function passwordEyeIconName(visibility: LoginPasswordVisibilityTP): 'eye-invisible' | 'eye' {
    return visibility === 'hidden' ? 'eye-invisible' : 'eye'
}

export function isPasswordVisibilityToggleTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element))
        return false

    return !!target.closest(`[${LOGIN_PASSWORD_TOGGLE_ATTR}="true"]`)
}

export function capsLockFromModifierEvent(event: { getModifierState?: (key: string) => boolean }): boolean | undefined {
    if (typeof event.getModifierState !== 'function')
        return undefined

    return event.getModifierState('CapsLock')
}

export function planLoginSubmit(params: { isAwaiting: boolean, username?: string, password?: string }): LoginSubmitPlanTP {
    if (!canStartLoginRequest(params.isAwaiting)) {
        return {
            shouldRunLoginRequest: false,
            shouldShowEmptyFieldsToast: false,
        }
    }

    if (hasBlankLoginField(params.username, params.password)) {
        return {
            submitAlert: 'emptyFields',
            shouldRunLoginRequest: false,
            shouldShowEmptyFieldsToast: false,
        }
    }

    return {
        shouldRunLoginRequest: true,
        shouldShowEmptyFieldsToast: false,
    }
}

export function planLoginRequestOutcome(params: { isSuccess: boolean, isError: boolean, status?: HttpStatusEnum }): LoginRequestOutcomePlanTP {
    if (params.isSuccess) {
        return {
            submitAlert: undefined,
            shouldShowCredentialsToast: false,
            shouldNavigateHome: true,
        }
    }

    if (!params.isError) {
        return {
            shouldShowCredentialsToast: false,
            shouldNavigateHome: false,
        }
    }

    const credentialsAlert = submitAlertAfterLoginFailure(params.status)
    if (credentialsAlert) {
        return {
            submitAlert: credentialsAlert,
            shouldShowCredentialsToast: false,
            shouldNavigateHome: false,
        }
    }

    return {
        submitAlert: undefined,
        shouldShowCredentialsToast: true,
        shouldNavigateHome: false,
    }
}

export function nextSubmitAlertAfterFieldChange(
    currentAlert: LoginSubmitAlertTP | undefined,
    username?: string,
    password?: string,
): LoginSubmitAlertTP | undefined {
    if (currentAlert !== 'emptyFields')
        return currentAlert

    if (!hasBlankLoginField(username, password))
        return undefined

    return 'emptyFields'
}

export function shouldSubmitLoginOnEnter(target: EventTarget | null): boolean {
    return !isPasswordVisibilityToggleTarget(target)
}

export function canSubmitLoginWithPasswordVisibility(visibility: LoginPasswordVisibilityTP): boolean {
    return visibility === 'hidden' || visibility === 'visible'
}

export function canSubmitLoginWithCapsLock(isCapsLockOn: boolean): boolean {
    return isCapsLockOn === true || isCapsLockOn === false
}
